import React from "react";
import { Helmet } from "react-helmet-async";

import { environment } from "../../config/environment";
import { Block } from "../../atoms/Layout/Block/Block";
import { GridCell } from "../../atoms/Layout/GridCell/GridCell";
import { GridRow } from "../../atoms/Layout/GridRow/GridRow";
import { H1 } from "../../atoms/Text/Headings";
import { HaveQuestions } from "../../molecules/HaveQuestions/HaveQuestions";
import { SearchGamesList } from "../../organisms/SearchGamesList/SearchGamesList";
import AARPAdUnitsMeta from "../../services/AARPAdUnitsMeta";
import { Game } from "../../models/Game";
import { PageSEOInterface } from "../../constants/PagesSEO";
import { CategoryDescription } from "../../molecules/CategoryDescription/CategoryDescription";
import { Categories, Pages } from "../../constants/Pages";
import { AtariBlock } from "../../molecules/AtariBlock/AtariBlock";
import { RetroGames } from "../../molecules/Recomendations/Retro";
import styles from "./Search.css";
import { ViafouraComponent } from "../../molecules/Viafoura/Viafoura";
import { categoryPageStructuredData } from "../../constants/SchemasSEO";
import { DesktopQuery, MobileQuery } from "../../atoms/Layout/Responsive";
import { RightRail } from "../../molecules/RightRail/RightRail";
import { RecommendedGames } from "./RecommendedGames";
import AARPAuthService from "../../services/AARPAuthService";
import { isServer } from "../../../utils";

type SearchProps = {
  caption: string;
  games: Game[];
  categorySEO?: PageSEOInterface;
  url?: string;
  isLoading?: boolean;
  allGames?: Game[];
  term?: string;
};

export const SearchTemplate = ({
  caption,
  games = [],
  categorySEO,
  url,
  isLoading = false,
  allGames = [],
  term,
}: SearchProps) => {
  let content: JSX.Element;

  const loginUrl = isServer ? "" : AARPAuthService.getLoginUrl();

  const renderHelmet = () =>
    categorySEO ? (
      <Helmet
        htmlAttributes={
          categorySEO.Arena5Slug === Categories.Juegos
            ? { lang: "es", class: "no-js" }
            : { lang: "en-US" }
        }
        title={categorySEO.MetaHTMLTitle}
        meta={[
          { name: "description", content: categorySEO.MetaHTMLDescription },
          {
            name: "robots",
            content:
              categorySEO.Arena5Slug === Categories.New
                ? "noindex,follow"
                : "index,follow",
          },
          { property: "og:title", content: categorySEO.OgTitle },
          { property: "og:description", content: categorySEO.OgDescription },
          { name: "vf:container_id", content: categorySEO.URLAlias },
          ...AARPAdUnitsMeta.category(categorySEO.Arena5Slug),
          ...[
            !categorySEO.MetaKeywords
              ? {}
              : { name: "keywords", content: categorySEO.MetaKeywords },
          ],
        ]}
        link={[{ rel: "canonical", href: url }]}
      >
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
    ) : (
      <Helmet
        title="Search Results for AARP Games"
        meta={[{ name: "robots", content: "noindex, follow" }]}
        link={[{ rel: "canonical", href: environment.PROD_HOST + "/" }]}
      />
    );

  const renderContent = () => {
    if (isLoading) return <ContentText>Loading...</ContentText>;

    if (!games.length && term && term.indexOf("games") != -1) {
      //caption = "Hello! Thank you for your search. Unfortunately, we don't have exactly what you are looking for but please enjoy any number of the games below";
      caption = "All Games";
      return <SearchGamesList games={allGames} />;
    } else if (!games.length) {
      return (
        <ContentText>
          <b>Welcome to your Games Overview! </b>
          Be sure to{" "}
          <LoginLink aria-label="login" href={loginUrl}>
            login
          </LoginLink>{" "}
          to view your Recently Played Games, or try something new and find a
          new favorite from the curated selection.
        </ContentText>
      );
    }
    return <SearchGamesList games={games} />;
  };

  //! If update this component
  //! Change the pageChangedDate for new current date
  const pageChangedDate = new Date("2022-12-07T10:52:01.670Z").toISOString();
  const schemaData = categoryPageStructuredData(
    url,
    categorySEO.MetaHTMLTitle,
    categorySEO.MetaHTMLDescription,
    games,
    pageChangedDate
  );

  const categoriesToHideViafoura = [
    Pages.All,
    Pages.RecentlyPlayed,
    Categories.New,
    Categories.Juegos,
  ];

  return (
    <React.Fragment>
      {renderHelmet()}

      <GridRow>
        <GridCell size={[8, 12]} direction="column">
          <Block paddingB="xl" paddingT="md">
            <H1 size="h3">{caption}</H1>
          </Block>
          {renderContent()}

          {categorySEO.Arena5Slug === Pages.RecentlyPlayed && (
            <RecommendedGames allGames={allGames} />
          )}

          {categorySEO && categorySEO.Arena5Slug === Categories.Atari && (
            <RetroGames allGames={allGames} />
          )}

          {categorySEO && (
            <CategoryDescription Arena5Slug={categorySEO.Arena5Slug} />
          )}

          {categorySEO && categorySEO.Arena5Slug === Categories.Atari && (
            <AtariBlock />
          )}

          {categorySEO &&
            !categoriesToHideViafoura.includes(categorySEO.Arena5Slug) && (
              <ViafouraComponent />
            )}

          <MobileQuery>
            <RightRail mode="Home" topGap={true} />
          </MobileQuery>

          <HaveQuestions />
        </GridCell>

        <DesktopQuery>
          <GridCell size={[4, 12]} direction="column">
            <RightRail mode="Category" />
          </GridCell>
        </DesktopQuery>
      </GridRow>
    </React.Fragment>
  );
};

const ContentText = (props: any) => (
  <div className={styles.contentText} {...props} />
);

const LoginLink = ({ children, ...props }: any) => (
  <a className={styles.loginLink} {...props}>
    {children}
  </a>
);
